import { trackEvent } from "@intercom/messenger-js-sdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import useCreateCase from "@/hooks/useCreateCase";
import useCreatePatient from "@/hooks/useCreatePatient";
import useTemplates from "@/hooks/useTemplates";
import useUploadRecording from "@/hooks/useUploadRecording";
import {
  FormSchema,
  FormValues,
  useQuickRecordAddCaseStore,
} from "@/store/quickRecordAddCaseStore";
import { useUserProvider } from "@/utils/UserProvider";
import {
  RECORD_TYPES,
  appShowNotification,
  deleteQuickRecordItemFromIndexedDB,
} from "@/utils/utils";
import { Case } from "@/API";
import QuickRecordUploadFailedModal from "./UploadFailedModal";
import QuickRecordUploadSuccessModal from "./UploadSuccessModal";

type QuickRecordAddCaseModalProps = {
  modalOpened: boolean;
  setModalOpened: (value: boolean | null) => void;
  recordingItem: any;
  refetchRecordings: () => Promise<void>;
  onViewCases?: () => void;
};

const QuickRecordAddCaseModal = (props: QuickRecordAddCaseModalProps) => {
  const {
    modalOpened,
    setModalOpened,
    recordingItem,
    refetchRecordings,
    onViewCases,
  } = props;
  const navigate = useNavigate();
  const { amplifyUser } = useUserProvider();
  const { patients } = useQuickRecordAddCaseStore();
  const { templates } = useTemplates();
  const [loading, setLoading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const { handleCreateCase } = useCreateCase();
  const { handleCreatePatient } = useCreatePatient();
  const { formData, setField, reset, touched, setTouched } =
    useQuickRecordAddCaseStore();
  const { handleUploadRecording } = useUploadRecording();
  const [caseDetail, setCaseDetail] = useState<any>();
  const [patient, setPatient] = useState<any>();

  useEffect(() => {
    if (templates.length > 0 && !formData.useTemplate) {
      setField("useTemplate", true);
      setField("templateId", templates[0].id);
    }
  }, [templates]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      FormSchema.parse(formData);
      if (formData.useTemplate && !formData.templateId) {
        appShowNotification({
          title: "Template selection is required",
          message: "Please select a template",
          type: "error",
        });
        return;
      }
      setLoading(true);
      let patientId = "";
      let patientLID = "";
      let caseId = "";
      let caseTemplateId = "";
      if (patient) {
        patientId = patient.id;
        patientLID = patient.lID;
      } else {
        const patientResponse = await handleCreatePatient(
          formData.patientName,
          formData.clientName,
        );
        if (!patientResponse.data.createPatient) {
          return;
        }
        const patientInfo = patientResponse.data.createPatient;
        patientId = patientInfo.id;
        patientLID = patientInfo.lID;
        setPatient(patientInfo);
        trackEvent("create_patient");
      }
      if (caseDetail) {
        caseId = caseDetail.id;
        caseTemplateId = caseDetail.caseTemplateId ?? "";
      } else {
        const caseResponse = await handleCreateCase(
          formData.caseTitle,
          formData.caseDescription || "",
          patientId,
          formData.useTemplate,
          formData.templateId || "",
          { ensureRelationship: true }
        );
        if (!caseResponse.data.createCase) {
          return;
        }
        trackEvent("create_case");
        const caseInfo = caseResponse.data.createCase as Case;
        caseId = caseInfo.id;
        caseTemplateId = caseInfo.caseTemplateId ?? "";
        setCaseDetail(caseInfo);
      }
      const objectToURL = URL.createObjectURL(recordingItem.blob);
      const uploaded = await buildAndUploadRecordingItem(
        recordingItem.name,
        patientId,
        patientLID,
        caseId,
        caseTemplateId,
        recordingItem.duration,
        objectToURL,
        recordingItem.blob,
      );
      if (!uploaded) {
        setUploadFailed(true);
      } else {
        setUploaded(true);
        setUploadFailed(false);
        await deleteQuickRecordItemFromIndexedDB(recordingItem.key);
        trackEvent("create_recording");
        reset();
        await refetchRecordings();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateExistingQuickRecord = async (
    recordingItem: any,
    patientId: string,
    caseId: string,
    caseTemplateId: string
  ) => {
    try {
      const { updateRecording } = await import("@/graphql/mutations");
      const { generateClient } = await import("aws-amplify/api");
      const client = generateClient();

      await client.graphql({
        query: updateRecording,
        variables: {
          input: {
            id: recordingItem.id,
            cID: caseId,
            useTemplate: !!caseTemplateId,
            _version: recordingItem._version
          }
        }
      });

      try {
        const { supabase } = await import("@/lib/supabaseClient");
        await supabase
          .from('case_recordings')
          .update({
            case_id: caseId,
            patient_id: patientId
          })
          .eq('id', recordingItem.id);
      } catch (supabaseError) {
        // Supabase update failed but Amplify update succeeded
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const buildAndUploadRecordingItem = async (
    filename: string,
    patientId: string,
    patientLID: string,
    caseId: string,
    caseTemplateId: string,
    duration: number,
    objectToURL: string,
    audioBlob: Blob,
  ) => {
    const storagePath =
      amplifyUser.id +
      "/" +
      patientLID +
      "/" +
      patientId +
      "/" +
      caseId +
      "/" +
      RECORD_TYPES.HISTORY +
      "/" +
      `${filename}.webm`;

    const newRecording = {
      name: filename,
      duration: duration,
      date: Date.now(),
      file: objectToURL,
      storagePath: storagePath,
      useTemplate: !!caseTemplateId,
      templateId: caseTemplateId ?? null,
      blob: audioBlob,
      caseId: caseId,
      patientId: patientId,
      patientLID: patientLID,
    };
    return await handleUploadRecording(
      newRecording,
      amplifyUser.audioRetentionPeriod,
    );
  };

  const isError = (field: keyof FormValues, errorMessage?: string) => {
    if (!touched[field]) return undefined;
    return FormSchema.shape[field].safeParse(formData[field]).success
      ? undefined
      : errorMessage;
  };

  return (
    <Modal
      opened={modalOpened}
      onClose={() => {
        reset();
        setModalOpened(null);
      }}
      centered
      title={"Add Case"}
    >
      <Box>
        <form onSubmit={handleSubmit}>
          <Stack gap="lg">
            <Stack gap="md">
              <Autocomplete
                label="Patient Name"
                withAsterisk
                placeholder="Enter Patient Name"
                data={patients.map((patient) => ({
                  value: patient.id,
                  label: patient.name,
                }))}
                onBlur={() => setTouched("patientName", true)}
                error={isError("patientName", "Patient name is required")}
                onChange={(value) => {
                  const selectedPatient = patients.find(
                    (patient) => patient.name === value,
                  );
                  if (selectedPatient) {
                    setPatient(selectedPatient);
                    setField("patientName", selectedPatient.name);
                    setField("clientName", selectedPatient.client!);
                    setCaseDetail(null);
                    setField("caseTitle", "");
                    setField("caseDescription", "");
                    setTouched("caseTitle", false);
                    setTouched("caseDescription", false);
                  } else {
                    setPatient(null);
                    setField("patientName", value ?? "");
                    setField("clientName", formData.clientName);
                  }
                }}
              />

              <TextInput
                label="Client Name"
                placeholder="Enter Client Name"
                withAsterisk
                radius="md"
                maxLength={200}
                disabled={!!patient}
                value={formData.clientName}
                onChange={(e) => setField("clientName", e.target.value)}
                onBlur={() => setTouched("clientName", true)}
                error={isError("clientName", "Client name is required")}
              />

              <Autocomplete
                label="Case Title"
                placeholder="Enter Case Title"
                withAsterisk
                radius="md"
                data={patient?.cases?.items?.map((item: any) => ({
                  value: item.id,
                  label: item.title,
                }))}
                value={formData.caseTitle}
                onBlur={() => setTouched("caseTitle", true)}
                error={isError("caseTitle", "Case title is required")}
                onChange={(value) => {
                  const selectedCase = patient?.cases?.items?.find(
                    (item: any) => item.title === value,
                  );
                  if (selectedCase) {
                    setCaseDetail(selectedCase);
                    setField("caseTitle", selectedCase.title);
                    setField("caseDescription", selectedCase.description);
                    if (selectedCase.caseTemplateId) {
                      setField("useTemplate", true);
                      setField("templateId", selectedCase.caseTemplateId);
                    } else {
                      setField("useTemplate", false);
                      setField("templateId", "");
                    }
                  } else {
                    setCaseDetail(null);
                    setField("caseTitle", value ?? "");
                    setField("caseDescription", formData.caseDescription);
                  }
                }}
              />

              <Stack gap={0}>
                <Group gap={3} mb={8}>
                  <Text size={"sm"} fw={600}>
                    Case Description
                  </Text>
                  <Text c="dimmed" size={"sm"}>
                    (Optional)
                  </Text>
                </Group>
                <Textarea
                  label={undefined}
                  placeholder="Enter Case Description"
                  description={undefined}
                  radius="md"
                  disabled={!!caseDetail}
                  minRows={3}
                  value={formData.caseDescription}
                  onChange={(e) => setField("caseDescription", e.target.value)}
                  onBlur={() => setTouched("caseDescription", true)}
                />
              </Stack>

              <Switch
                label="Use Template"
                labelPosition="left"
                disabled={!!caseDetail}
                checked={formData.useTemplate}
                onChange={(event) => {
                  setField("useTemplate", event.currentTarget.checked);
                  if (!event.currentTarget.checked) {
                    setField("templateId", "");
                    setTouched("templateId", false);
                  }
                }}
                size="md"
              />
              {formData.useTemplate && (
                <Select
                  label="Template"
                  placeholder="Select Template"
                  data={templates.map((template) => ({
                    value: template.id,
                    label: template.name,
                  }))}
                  disabled={!!caseDetail}
                  radius="md"
                  withAsterisk
                  onBlur={() => setTouched("templateId", true)}
                  value={formData.templateId}
                  onChange={(value) => setField("templateId", value ?? "")}
                  error={
                    formData.useTemplate &&
                    touched.templateId &&
                    !formData.templateId &&
                    "Template selection is required"
                  }
                />
              )}
            </Stack>
            <Button
              color="manta"
              w={"100%"}
              h={50}
              fw={500}
              radius={50}
              loading={loading}
              disabled={loading}
              type="submit"
            >
              Save
            </Button>
          </Stack>
        </form>
      </Box>
      <QuickRecordUploadSuccessModal
        modalOpened={uploaded}
        setModalOpened={(value) => {
          setUploaded(value ?? false);
        }}
        onRecord={() => {
          setModalOpened(null);
          setCaseDetail(null);
        }}
        onViewCases={() => {
          setModalOpened(null);
          setCaseDetail(null);
          setTimeout(() => {
            onViewCases?.();
          }, 500);
          setTimeout(() => {
            navigate(`/case-detail/${caseDetail.id}`, {
              state: {
                ...caseDetail,
                patientId: caseDetail.patientId,
                patient: patient,
                patientCase: {
                  ...caseDetail,
                  patientId:
                    patient.id || caseDetail.patientId || caseDetail.pID,
                },
              },
            });
          }, 1000);
        }}
      />
      <QuickRecordUploadFailedModal
        modalOpened={uploadFailed}
        onCancel={async () => {
          setUploadFailed(false);
          await deleteQuickRecordItemFromIndexedDB(recordingItem.key);
          reset();
          setModalOpened(null);
          await refetchRecordings();
        }}
        onTryAgain={async () => {
          setUploadFailed(false);
          setTimeout(() => {
            handleSubmit();
          }, 1000);
        }}
      />
    </Modal>
  );
};

export default QuickRecordAddCaseModal;
